import React from 'react'

import { IconButton } from '@mui/material'
import { InfoIcon } from 'lucide-react'

import { useTermSheet } from 'components/UI/TermSheetProvider'
import { glossaryTerm } from 'docs/glossary'

export interface TermInfoProps {
  /** Glossary entry id, e.g. `'provenance'`. Renders nothing if it does not resolve. */
  termId: string
}

/**
 * The `(i)` that opens a term's definition.
 *
 * Small and secondary on purpose: it sits beside a label the reader is already looking
 * at, and it is an offer rather than an instruction. Anything louder would compete with
 * the content it annotates on a 375px screen where that content is the point.
 *
 * ## It renders NOTHING for an id this app cannot explain
 *
 * The glossary artifact here carries the consumer floor only, so a brand-floor id
 * (`activation-trigger`, `tag`, `scan-status-invalid`) resolves to nothing. Returning
 * `null` makes that case invisible rather than a button that opens an empty sheet, which
 * is what lets call sites be written declaratively: the certificate status badge can ask
 * for `certificate-status-${status}` for every status and get an affordance only on the
 * ones the glossary actually defines, with no per-status list to keep in sync.
 *
 * ## `stopPropagation` is load-bearing
 *
 * Every site this is used at is inside something clickable — a `FieldRow` that navigates,
 * a table row, a badge. Without it, tapping the `(i)` would ALSO fire the row, so the
 * definition would open on a page the reader was in the middle of leaving.
 */
export const TermInfo = ({ termId }: TermInfoProps) => {
  const { openTerm } = useTermSheet()
  const entry = glossaryTerm(termId)

  if (!entry) return null

  return (
    <IconButton
      size='small'
      aria-label={`What does "${entry.term}" mean?`}
      onClick={event => {
        event.stopPropagation()
        event.preventDefault()
        openTerm(entry.id)
      }}
      sx={{
        // No background at rest. This is an annotation, not a control competing with the
        // row it sits in, and the colour is what says "secondary".
        p: '4px',
        color: 'text.secondary',
        '@media (hover: hover)': {
          '&:hover': { backgroundColor: 'action.hover', color: 'text.primary' },
        },
        /*
          The tap target is 44x44; the INK is 22x22.
          A 14px glyph with 4px of padding measures 22x22 on screen, which is below even
          WCAG 2.5.8's 24px floor, let alone the 44px both platforms ask for. That is bad
          anywhere and worse here: this app is only ever used on a phone, by someone
          holding the item in their other hand, and on the History rows this button sits
          about 12px from a chevron that navigates to a different SCREEN. A miss does not
          do nothing, it takes the reader somewhere they did not ask to go.
          Extending the hit area with an inset pseudo-element rather than by growing the
          padding is what keeps the two independent: padding would push the row's other
          contents around and change the visual weight of an element that is deliberately
          quiet, where `inset: -11px` is invisible and costs no layout at all. The button
          paints its own hover on the 22px box, so the larger target never shows itself.
        */
        position: 'relative',
        '&::after': { content: '""', position: 'absolute', inset: '-11px' },
      }}
    >
      <InfoIcon size={14} strokeWidth={1.5} />
    </IconButton>
  )
}
